import { createStore } from '@aibees/shared/store';
import { objSanitizeNilValues } from '@aibees/shared/utils';
import { isEqual } from 'lodash-es';
import { createJSONStorage } from 'zustand/middleware';
import type { SearchBrowseParams } from '../../types';
import type { SearchParamsStoreData as StoreData } from './types';

const EMPTY_SCOPE = {} as Partial<SearchBrowseParams>;

const SEARCH_PARAMS_REHYDRATED_EVENT = 'search-params-rehydrated';
export const SEARCH_PARAMS_STORE_KEY = 'elephant-search-params';

/**
 * When merging params, avoid overwriting a longer composite value with a
 * shorter/stale one (e.g. "accounting" overwriting "accounting|2>", or
 * "|1>4" overwriting "accounting|1>4"). Mutates `merged` in place.
 */
function preventStaleCompositeOverwrite(
  merged: Record<string, unknown>,
  params: Record<string, unknown>,
  updatesRecord: Record<string, unknown>
): void {
  for (const key of Object.keys(updatesRecord)) {
    const current = params[key];
    const incoming = updatesRecord[key];
    if (
      typeof current === 'string' &&
      typeof incoming === 'string' &&
      current.length > incoming.length
    ) {
      if (current.startsWith(incoming) || current.endsWith(incoming)) {
        merged[key] = current;
      }
    }
  }
}

const useSearchParamsStore = createStore<StoreData>(
  'search-params',
  (set, get) => ({
    paramsByScope: {
      company: { ...EMPTY_SCOPE },
      people: { ...EMPTY_SCOPE },
      employees: { ...EMPTY_SCOPE },
    },
    previousParamsByScope: {},
    skipHistoryForNextSync: false,

    setParamsForScope: (scope, updates, options) => {
      if (updates == null) return;
      const { paramsByScope, previousParamsByScope } = get();
      const params = paramsByScope[scope] ?? EMPTY_SCOPE;
      const updatesRecord = updates as Record<string, unknown>;
      let merged: Record<string, unknown>;
      if (options?.replace) {
        merged = { ...updatesRecord };
      } else {
        merged = { ...params, ...updatesRecord };
        preventStaleCompositeOverwrite(
          merged,
          params as Record<string, unknown>,
          updatesRecord
        );
      }
      const newParams = objSanitizeNilValues(
        merged
      ) as Partial<SearchBrowseParams>;
      if (isEqual(newParams, params)) return;
      set({
        paramsByScope: {
          ...paramsByScope,
          [scope]: newParams,
        },
        previousParamsByScope: {
          ...previousParamsByScope,
          [scope]: params,
        },
        skipHistoryForNextSync: options?.skipHistory === true,
      });
    },

    setParamForScope: (scope, key, value, options) => {
      const { paramsByScope, previousParamsByScope } = get();
      const params = paramsByScope[scope] ?? EMPTY_SCOPE;
      const next = { ...params, [key]: value };
      const sanitized = objSanitizeNilValues(
        next
      ) as Partial<SearchBrowseParams>;
      if (isEqual(sanitized, params)) return;
      set({
        paramsByScope: { ...paramsByScope, [scope]: sanitized },
        previousParamsByScope: {
          ...previousParamsByScope,
          [scope]: params,
        },
        skipHistoryForNextSync: options?.skipHistory === true,
      });
    },

    removeParamForScope: (scope, key, options) => {
      const { paramsByScope, previousParamsByScope } = get();
      const params = paramsByScope[scope] ?? EMPTY_SCOPE;
      const next = { ...params };
      delete next[key];
      const sanitized = objSanitizeNilValues(
        next
      ) as Partial<SearchBrowseParams>;
      if (isEqual(sanitized, params)) return;
      set({
        paramsByScope: { ...paramsByScope, [scope]: sanitized },
        previousParamsByScope: {
          ...previousParamsByScope,
          [scope]: params,
        },
        skipHistoryForNextSync: options?.skipHistory === true,
      });
    },

    removeAllParamsForScope: (scope) => {
      const { paramsByScope, previousParamsByScope } = get();
      set({
        paramsByScope: {
          ...paramsByScope,
          [scope]: EMPTY_SCOPE,
        },
        previousParamsByScope: {
          ...previousParamsByScope,
          [scope]: paramsByScope[scope],
        },
      });
    },

    replaceParamsForScope: (scope, values) => {
      const { paramsByScope, previousParamsByScope } = get();
      const params = paramsByScope[scope] ?? EMPTY_SCOPE;
      const sanitized = objSanitizeNilValues(
        values ?? {}
      ) as Partial<SearchBrowseParams>;
      set({
        paramsByScope: { ...paramsByScope, [scope]: sanitized },
        previousParamsByScope: {
          ...previousParamsByScope,
          [scope]: params,
        },
      });
    },

    clearSkipHistoryForNextSync: () => set({ skipHistoryForNextSync: false }),
  }),
  {
    persisted: true,
    persist: {
      name: SEARCH_PARAMS_STORE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state): Partial<StoreData> =>
        ({
          paramsByScope: {
            company: state.paramsByScope.company,
            people: state.paramsByScope.people,
            employees: state.paramsByScope.employees,
          },
        } as Partial<StoreData>),
      merge: (persistedState, currentState) => ({
        ...currentState,
        paramsByScope: {
          ...currentState.paramsByScope,
          ...(persistedState as Partial<StoreData>).paramsByScope,
        },
      }),
      onRehydrateStorage: () => () => {
        if (typeof window !== 'undefined') {
          (
            window as Window & { __searchParamsStoreRehydrated?: boolean }
          ).__searchParamsStoreRehydrated = true;
          window.dispatchEvent(new CustomEvent(SEARCH_PARAMS_REHYDRATED_EVENT));
        }
      },
    },
  }
);

export default useSearchParamsStore;
export { SEARCH_PARAMS_REHYDRATED_EVENT };
